/**
 * Vergroesserte 9:16-Karte am Desktop (Benedikt/Jan 28.09.2026, Vorbild
 * Instagram-Stories).
 *
 * Der Viewer baut die Karte als Handy mit hoechstens 600 px Layout-Breite
 * (darueber kippen die Slide-Layouts) und zieht die fertige Karte dann per
 * `transform: scale(k)` auf die Fensterhoehe. Alles, was der Renderer nach
 * der Pixeldichte bemisst, muss dann mit `dpr × k` rechnen, sonst wird es nur
 * weich hochgezogen:
 *   - der WebGL-Puffer (setPixelRatio),
 *   - die Bildbreite, die beim /img-Dienst bestellt wird,
 *   - die Textur-Qualitaetsstufe,
 *   - die Wahl der Video-Fassung (Mobilfassung reicht nur fuer kleine Buehnen).
 *
 * Der Faktor liegt am `window`, weil Viewer und Renderer getrennte Module
 * (und im Dev-Server ggf. getrennte Instanzen) sind. Ohne Karte bleibt er 1.
 */

const SCHLUESSEL = "__scrollyKartenSkala";

/** Deckel fuer die wirksame Pixeldichte (GPU-Speicher). */
const MAX_WIRKSAME_DPR = 4;

/** Vom Viewer gesetzt: wie stark die Karte gerade vergroessert ist (>= 1). */
export function setzeKartenSkala(k: number): void {
    if (typeof window === "undefined") return;
    (window as unknown as Record<string, number>)[SCHLUESSEL] =
        Number.isFinite(k) && k > 1 ? k : 1;
}

/** Aktueller Vergroesserungsfaktor der Karte; 1 = keine Vergroesserung. */
export function kartenSkala(): number {
    if (typeof window === "undefined") return 1;
    const k = Number((window as unknown as Record<string, number>)[SCHLUESSEL]);
    return Number.isFinite(k) && k > 1 ? k : 1;
}

/**
 * Pixeldichte, mit der Puffer, Bilder und Videos bemessen werden: echte
 * Geraete-Pixeldichte mal Karten-Vergroesserung, gedeckelt.
 */
export function wirksameDpr(dpr: number): number {
    const basis = Number.isFinite(dpr) && dpr > 0 ? dpr : 1;
    const k = kartenSkala();
    return k === 1 ? basis : Math.min(basis * k, Math.max(basis, MAX_WIRKSAME_DPR));
}

/**
 * Steckt der Rahmen in einem gleichmaessigen `transform: scale()`? Nur dann
 * weichen Sicht- und Layout-Mass auf BEIDEN Achsen um denselben Faktor ab.
 * Aendert sich nur eine Achse (Hoehe springt, Scrollleiste nimmt Breite),
 * ist das eine echte Groessenaenderung, kein Transform.
 */
export function rahmenGleichmaessigSkaliert(
    sichtBreite: number,
    sichtHoehe: number,
    layoutBreite: number,
    layoutHoehe: number,
): boolean {
    if (!(layoutBreite > 0) || !(layoutHoehe > 0)) return false;
    const sx = sichtBreite / layoutBreite;
    const sy = sichtHoehe / layoutHoehe;
    if (!Number.isFinite(sx) || !Number.isFinite(sy)) return false;
    return Math.abs(sx - 1) >= 0.01 && Math.abs(sx - sy) < 0.01;
}
